/**
 * Settings → Privacy.
 *
 * Toggles:
 *   - publicProfileEnabled — master switch; enables public profile page.
 *     Cosmic ID footnote shows the short ID that will be the public address.
 *   - selfieStorageEnabled — flipping OFF opens ConfirmModal; on confirm,
 *     PATCH /me with {privacyPrefs:{selfieStorageEnabled:false}} which the
 *     backend treats as a trigger to enqueue the selfie-purge worker (T18/T19).
 *
 * Data-export button: GET /me/data-export → blob → browser download (T26).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell';
import { Toggle } from '../../components/settings/Toggle';
import { ConfirmModal } from '../../components/settings/ConfirmModal';
import { useMe, type PrivacyPrefs } from '../../hooks/useMe';
import { usePatchMePrefs } from '../../hooks/usePatchMePrefs';
import styles from './Privacy.module.css';

export function Privacy(): JSX.Element {
  const { t } = useTranslation(['settings', 'common']);
  const { data: me, isLoading, isError } = useMe();
  const patch = usePatchMePrefs();
  const [showSelfieConfirm, setShowSelfieConfirm] = useState(false);

  const prefs = me?.privacyPrefs;

  const togglePref = (key: keyof PrivacyPrefs, next: boolean): void => {
    patch.mutate({ privacyPrefs: { [key]: next } as Partial<PrivacyPrefs> });
  };

  const onSelfieToggle = (next: boolean): void => {
    if (!next) {
      setShowSelfieConfirm(true);
    } else {
      togglePref('selfieStorageEnabled', true);
    }
  };

  const confirmSelfieOff = (): void => {
    setShowSelfieConfirm(false);
    togglePref('selfieStorageEnabled', false);
  };

  const cosmicIdShort = me?.id ? me.id.slice(-8) : '—';

  return (
    <MeSubpageShell title={t('privacy.title', { ns: 'settings' })} data-testid="settings-privacy-page">
      {isLoading && <p data-testid="privacy-loading">{t('states.loading', { ns: 'common' })}</p>}
      {isError && (
        <p role="alert" data-testid="privacy-error">{t('privacy.saveError', { ns: 'settings' })}</p>
      )}
      {!isLoading && !isError && prefs && (
        <>
          {/* Public profile opt-in toggle */}
          <section className={styles.section} aria-label={t('privacy.publicProfile.label', { ns: 'settings' })}>
            <div className={styles.row}>
              <div className={styles.rowText}>
                <p className={styles.rowLabel}>{t('privacy.publicProfile.label', { ns: 'settings' })}</p>
                <p className={styles.rowDescription}>{t('privacy.publicProfile.description', { ns: 'settings' })}</p>
              </div>
              <Toggle
                checked={me?.publicProfileEnabled ?? false}
                onChange={(next) => patch.mutate({ publicProfileEnabled: next })}
                label={t('privacy.publicProfile.label', { ns: 'settings' })}
                data-testid="privacy-toggle-publicProfileEnabled"
              />
            </div>
            <p className={styles.footnote} data-testid="privacy-cosmic-id-footnote">
              {t('privacy.cosmicIdFootnote', { ns: 'settings', id: cosmicIdShort })}
            </p>

            {/* Sub-toggles — only shown when public profile is enabled */}
            {me?.publicProfileEnabled && (
              <>
                <div className={styles.row}>
                  <div className={styles.rowText}>
                    <p className={styles.rowLabel}>{t('privacy.namePublic.label', { ns: 'settings' })}</p>
                    <p className={styles.rowDescription}>{t('privacy.namePublic.description', { ns: 'settings' })}</p>
                  </div>
                  <Toggle
                    checked={prefs?.namePublic ?? false}
                    onChange={(next) => togglePref('namePublic', next)}
                    label={t('privacy.namePublic.label', { ns: 'settings' })}
                    data-testid="privacy-toggle-namePublic"
                  />
                </div>
                <div className={styles.row}>
                  <div className={styles.rowText}>
                    <p className={styles.rowLabel}>{t('privacy.avatarPublic.label', { ns: 'settings' })}</p>
                    <p className={styles.rowDescription}>{t('privacy.avatarPublic.description', { ns: 'settings' })}</p>
                  </div>
                  <Toggle
                    checked={prefs?.avatarPublic ?? false}
                    onChange={(next) => togglePref('avatarPublic', next)}
                    label={t('privacy.avatarPublic.label', { ns: 'settings' })}
                    data-testid="privacy-toggle-avatarPublic"
                  />
                </div>
              </>
            )}
          </section>

          <section className={styles.section} aria-label={t('privacy.selfieStorage.label', { ns: 'settings' })}>
            <div className={styles.row}>
              <div className={styles.rowText}>
                <p className={styles.rowLabel}>{t('privacy.selfieStorage.label', { ns: 'settings' })}</p>
                <p className={styles.rowDescription}>
                  {t('privacy.selfieStorage.description', { ns: 'settings' })}
                </p>
              </div>
              <Toggle
                checked={prefs.selfieStorageEnabled}
                onChange={onSelfieToggle}
                label={t('privacy.selfieStorage.label', { ns: 'settings' })}
                data-testid="privacy-toggle-selfieStorageEnabled"
              />
            </div>
          </section>


          {patch.isError && (
            <p role="alert" data-testid="privacy-save-error">
              {t('privacy.saveError', { ns: 'settings' })}
            </p>
          )}
        </>
      )}

      {showSelfieConfirm && (
        <ConfirmModal
          title={t('privacy.selfieStorage.confirmTitle', { ns: 'settings' })}
          body={t('privacy.selfieStorage.confirmBody', { ns: 'settings' })}
          confirmLabel={t('privacy.selfieStorage.confirmAction', { ns: 'settings' })}
          cancelLabel={t('actions.cancel', { ns: 'common' })}
          variant="danger"
          onConfirm={confirmSelfieOff}
          onCancel={() => setShowSelfieConfirm(false)}
          data-testid="privacy-selfie-confirm"
        />
      )}
    </MeSubpageShell>
  );
}
